import React, { useRef } from 'react';import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

/**
 * Shared pagination footer — single source of truth for BOTH client-side and
 * server-side paginated tables/lists across the whole app.
 *
 * Props:
 *  - page              current page (1-indexed by default, 0-indexed when zeroIndexed=true)
 *  - setPage           setter (number OR updater fn)
 *  - totalItems        total count (filtered length in client mode, server total in server mode)
 *  - itemsPerPage      current page size (default 25)
 *  - setItemsPerPage   OPTIONAL — when provided, renders a "Rows per page" dropdown
 *  - pageSizeOptions   app-wide default [5, 10, 25, 50, 100, 200]
 *  - testIdPrefix      e.g. "event-rules", "drivers", "device-mgmt"
 *  - scrollOnChange    default true — smooth-scrolls the enclosing card
 *                      (or a custom scrollTargetRef) into view on page /
 *                      pagesize change, so the LIST's top edge (not the
 *                      top of the whole page) is what appears.
 *  - scrollTargetRef   OPTIONAL React ref to the specific element that
 *                      should be scrolled into view. Defaults to the
 *                      nearest ancestor Card (data-testid ending in
 *                      "-card"), then the footer's direct parent.
 *  - zeroIndexed       default false. When true, `page` and `setPage` use
 *                      0-indexed pages (useful for server-side pagination
 *                      code that stores pageIndex 0..N-1).
 *  - alwaysShow        default false. When true the footer is rendered even
 *                      when totalItems is 0 or below the smallest page size,
 *                      so table cards keep a consistent height across loading
 *                      / empty / populated states.
 *  - disabled          default false. Disables Prev/Next/PageSize buttons
 *                      (typically true while a server fetch is loading).
 *
 * Auto-hides ONLY when totalItems <= smallest pageSizeOption AND no selector
 * is shown. When a selector is shown, the footer stays visible so users can
 * bump the page size. Set `alwaysShow` to bypass the hide entirely.
 */
export const PaginationFooter = ({
  page,
  setPage,
  totalItems,
  itemsPerPage = 25,
  setItemsPerPage = null,
  pageSizeOptions = [5, 10, 25, 50, 100, 200],
  testIdPrefix = 'pagination',
  scrollOnChange = true,
  scrollTargetRef = null,
  zeroIndexed = false,
  disabled = false,
  alwaysShow = false,
}) => {
  const footerRef = useRef(null);
  const hasSelector = typeof setItemsPerPage === 'function';
  const smallestOpt = pageSizeOptions[0] ?? itemsPerPage;
  // Hide entirely if fewer than the smallest option AND no selector to render
  if (!alwaysShow && !hasSelector && totalItems <= itemsPerPage) return null;
  if (!alwaysShow && hasSelector && totalItems <= smallestOpt) return null;

  // Internal 1-indexed math (display-friendly). Callers using 0-indexed pages
  // are transparently converted at both the read (page → oneIndexed) and
  // write (setPage) boundaries.
  const oneIndexed = zeroIndexed ? (page ?? 0) + 1 : (page ?? 1);
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const safePage = Math.min(Math.max(1, oneIndexed), totalPages);
  const pageStart = totalItems === 0 ? 0 : (safePage - 1) * itemsPerPage;
  const pageEnd = Math.min(pageStart + itemsPerPage, totalItems);

  // Emit page value in whatever indexing the caller expects.
  const emitPage = (nextOneIndexed) => {
    if (zeroIndexed) {
      setPage(Math.max(0, nextOneIndexed - 1));
    } else {
      setPage(Math.max(1, nextOneIndexed));
    }
  };

  // Smoothly scroll the LIST (not the whole page) to the top when the
  // page or page-size changes. Accounts for the app's sticky top header
  // so the list's top row lands BELOW the header (not hidden behind it),
  // which was making page transitions look like they only scrolled 85%.
  //
  //  1. If the caller passes `scrollTargetRef`, that element is used
  //     (typically the enclosing Card so the card header is visible too).
  //  2. Otherwise we walk up from the footer to the nearest ancestor
  //     Card (elements with a data-testid ending in "-card").
  //  3. Fallback: the footer's direct parent (the list row container).
  //
  // When the target is inside a Dialog / Popover we defer to
  // scrollIntoView so the dialog's own scroll container is used. For
  // ordinary pages we compute the target Y manually and offset by the
  // sticky app-header height so the row is fully visible.
  const scrollListToTop = () => {
    if (!scrollOnChange) return;
    requestAnimationFrame(() => {
      let target = scrollTargetRef?.current || null;
      if (!target) {
        // App-wide standard: land on the "0 selected" selection bar when the
        // table provides one (data-testid `{prefix}-selection-bar`).
        target = document.querySelector(`[data-testid="${testIdPrefix}-selection-bar"]`);
      }
      if (!target && footerRef.current) {
        let el = footerRef.current.parentElement;
        while (el && el !== document.body) {
          const tid = el.getAttribute?.('data-testid') || '';
          if (tid.endsWith('-card') || el.tagName === 'ARTICLE') { target = el; break; }
          el = el.parentElement;
        }
        if (!target) target = footerRef.current.parentElement;
      }
      if (!target) return;

      const insideDialog = typeof target.closest === 'function' && target.closest('[role="dialog"]');
      if (insideDialog) {
        // Inside a modal / dialog: use the browser's native scroll-into-view
        // so it targets the dialog's inner scroll container. Dialogs don't
        // have the app's sticky header, so no offset is needed.
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }

      const headerEl = document.querySelector('[data-testid="header"]');
      const headerHeight = headerEl?.offsetHeight || 0;
      const rect = target.getBoundingClientRect();
      // Small extra breathing room so the row isn't flush with the sticky header.
      const desiredTop = Math.max(0, window.scrollY + rect.top - headerHeight - 8);
      window.scrollTo({ top: desiredTop, behavior: 'smooth' });
    });
  };

  // Blur the clicked button BEFORE starting the smooth scroll. If it stays
  // focused and `disabled={loading}` lands mid-animation, Chromium force-blurs
  // it and cancels the in-flight smooth scroll, stranding the page short of
  // the target (seen on Shared Evidence / Fleet with URL-based page state).
  // Skipped when scrollOnChange is false (e.g. inside modals): there is no
  // scroll to protect, and the blur makes focus fall back onto the dialog
  // container, flashing the browser focus ring around the modal edge.
  const goPrev = (e) => { if (scrollOnChange) e?.currentTarget?.blur?.(); emitPage(safePage - 1); scrollListToTop(); };
  const goNext = (e) => { if (scrollOnChange) e?.currentTarget?.blur?.(); emitPage(safePage + 1); scrollListToTop(); };
  const changePageSize = (v) => {
    // Same focus guard as goPrev/goNext — the Select trigger is focused and
    // also gets `disabled` while loading, which would cancel the smooth scroll.
    if (scrollOnChange && document.activeElement instanceof HTMLElement) document.activeElement.blur();
    setItemsPerPage(parseInt(v, 10));
    // Reset to first page whenever the size changes (0-indexed → 0, 1-indexed → 1)
    if (zeroIndexed) setPage(0); else setPage(1);
    scrollListToTop();
  };

  return (
    <div ref={footerRef} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t" data-testid={`${testIdPrefix}-pagination`}>
      <div className="flex items-center gap-3">
        <p className="text-xs text-muted-foreground" data-testid={`${testIdPrefix}-pagination-info`}>
          Showing {pageStart + (totalItems === 0 ? 0 : 1)}–{pageEnd} of {totalItems}
        </p>
        {hasSelector && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Rows per page</span>
            <Select
              value={String(itemsPerPage)}
              onValueChange={changePageSize}
              disabled={disabled}
            >
              <SelectTrigger className="h-8 w-20 dark:bg-[#172031]" data-testid={`${testIdPrefix}-pagination-pagesize`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((n) => (
                  <SelectItem key={n} value={String(n)} data-testid={`${testIdPrefix}-pagination-pagesize-${n}`}>{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      <div className="flex items-center gap-1">
        <Button
          size="sm"
          variant="outline"
          onClick={goPrev}
          disabled={disabled || safePage <= 1}
          data-testid={`${testIdPrefix}-pagination-prev`}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="text-xs px-3 min-w-[5rem] text-center" data-testid={`${testIdPrefix}-pagination-page`}>
          Page {safePage} of {totalPages}
        </span>
        <Button
          size="sm"
          variant="outline"
          onClick={goNext}
          disabled={disabled || safePage >= totalPages}
          data-testid={`${testIdPrefix}-pagination-next`}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

/**
 * usePersistedPageSize — small hook that hydrates the initial page-size
 * from localStorage (if the user has previously changed it on that table),
 * otherwise falls back to the caller-provided default. Whenever the page
 * size changes it is written back to localStorage under a namespaced key,
 * so each table remembers the user's preferred rows-per-page independently.
 *
 *   const [pageSize, setPageSize] = usePersistedPageSize('command-history', 25);
 *
 * The key convention is `pgsize_${scope}`; callers should pass the same
 * value they use for `testIdPrefix` so keys stay stable across renames.
 */
export const usePersistedPageSize = (scope, defaultValue = 25) => {
  const storageKey = `pgsize_${scope}`;
  const [value, setValue] = React.useState(() => {
    if (typeof window === 'undefined') return defaultValue;
    try {
      const raw = window.localStorage.getItem(storageKey);
      const parsed = raw ? parseInt(raw, 10) : NaN;
      return Number.isFinite(parsed) && parsed > 0 ? parsed : defaultValue;
    } catch { return defaultValue; }
  });
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    try { window.localStorage.setItem(storageKey, String(value)); } catch { /* quota / private-mode */ }
  }, [storageKey, value]);
  return [value, setValue];
};

/**
 * Helper: slice a filtered array to the current page (client-side pagination).
 * Also clamps the page number if the filter shrinks the list.
 */
export const paginateArray = (items, page, itemsPerPage = 25) => {
  const totalPages = Math.max(1, Math.ceil((items?.length || 0) / itemsPerPage));
  const safePage = Math.min(Math.max(1, page || 1), totalPages);
  const start = (safePage - 1) * itemsPerPage;
  return (items || []).slice(start, start + itemsPerPage);
};
